// Google Analytics configuration with lazy loading
interface AnalyticsConfig {
  measurementId: string;
  debugMode?: boolean;
  enableEcommerce?: boolean;
  enableEnhancedEcommerce?: boolean;
}

class GoogleAnalytics {
  private config: AnalyticsConfig;
  private isLoaded = false;
  private isInitialized = false;
  private queue: Array<() => void> = [];
  private loadTimeout: NodeJS.Timeout | null = null;

  constructor(config: AnalyticsConfig) {
    this.config = config;
    this.initLazyLoading();
  }

  // Initialize lazy loading after 5 seconds - better performance
  private initLazyLoading() {
    // Wait for 5 seconds before loading GA - reduce initial load
    this.loadTimeout = setTimeout(() => {
      this.loadGoogleAnalytics();
    }, 5000);
  }

  // Load Google Analytics script
  private loadGoogleAnalytics() {
    if (this.isLoaded) return;

    try {
      // Create script element
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.config.measurementId}`;
      
      // Add to head
      document.head.appendChild(script);

      // Initialize gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };

      // Configure gtag
      window.gtag('js', new Date());
      window.gtag('config', this.config.measurementId, {
        debug_mode: this.config.debugMode || false,
        send_page_view: true,
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });

      this.isLoaded = true;
      this.isInitialized = true;

      // Process queued events
      this.processQueue();

      console.log('🚀 Google Analytics loaded successfully (lazy loading)');
    } catch (error) {
      console.error('❌ Failed to load Google Analytics:', error);
    }
  }

  // Process queued events
  private processQueue() {
    if (!this.isInitialized) return;

    while (this.queue.length > 0) {
      const event = this.queue.shift();
      if (event) event();
    }
  }

  // Queue event if GA not loaded yet
  private queueOrExecute(event: () => void) {
    if (this.isInitialized) {
      event();
    } else {
      this.queue.push(event);
    }
  }

  // Track page view
  pageview(path: string, title?: string) {
    this.queueOrExecute(() => {
      window.gtag('config', this.config.measurementId, {
        page_path: path,
        page_title: title || document.title,
      });
    });
  }

  // Track custom event
  event(action: string, category: string, label?: string, value?: number) {
    this.queueOrExecute(() => {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    });
  }

  // Track user engagement
  trackEngagement(action: string, details?: Record<string, any>) {
    this.queueOrExecute(() => {
      window.gtag('event', action, {
        event_category: 'engagement',
        ...details,
      });
    });
  }

  // Track video interactions
  trackVideo(action: string, videoId: string, details?: Record<string, any>) {
    this.queueOrExecute(() => {
      window.gtag('event', action, {
        event_category: 'video',
        video_id: videoId,
        ...details,
      });
    });
  }

  // Track payment events
  trackPayment(action: string, paymentMethod: string, amount?: number, currency?: string) {
    this.queueOrExecute(() => {
      window.gtag('event', action, {
        event_category: 'payment',
        payment_method: paymentMethod,
        value: amount,
        currency: currency || 'USD',
      });
    });
  }

  // Track form submissions
  trackForm(action: string, formName: string, success: boolean) {
    this.queueOrExecute(() => {
      window.gtag('event', action, {
        event_category: 'form',
        form_name: formName,
        success: success,
      });
    });
  }

  // Track user actions
  trackUserAction(action: string, category: string, details?: Record<string, any>) {
    this.queueOrExecute(() => {
      window.gtag('event', action, {
        event_category: category,
        ...details,
      });
    });
  }

  // Set user properties
  setUserProperties(properties: Record<string, any>) {
    this.queueOrExecute(() => {
      window.gtag('config', this.config.measurementId, {
        custom_map: properties,
      });
    });
  }

  // Track conversion
  trackConversion(conversionId: string, value?: number, currency?: string) {
    this.queueOrExecute(() => {
      window.gtag('event', 'conversion', {
        send_to: `${this.config.measurementId}/${conversionId}`,
        value: value,
        currency: currency || 'USD',
      });
    });
  }

  // Get analytics status
  getStatus() {
    return {
      isLoaded: this.isLoaded,
      isInitialized: this.isInitialized,
      queueLength: this.queue.length,
      config: this.config,
    };
  }

  // Force load GA (for immediate tracking)
  forceLoad() {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }
    this.loadGoogleAnalytics();
  }

  // Cleanup
  destroy() {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }
    this.queue = [];
    this.isLoaded = false;
    this.isInitialized = false;
  }
}

// Global type declarations
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Export singleton instance
export const analytics = new GoogleAnalytics({
  measurementId: import.meta.env.VITE_GA_MEASUREMENT_ID || '',
  debugMode: import.meta.env.DEV,
  enableEcommerce: true,
  enableEnhancedEcommerce: true,
});

// Export class for custom instances
export { GoogleAnalytics };
